import { literalType } from "../../../api/content/processTerms";
import type { WorkflowInputSummary, WorkflowSummary } from "../../../api/content/workflowsApi";

/**
 * The declared inputs of exactly the Workflow version a start names, among a content version's listed Workflows;
 * `null` where that version isn't listed. A fork onto a newer content version keeps its parent's start, and the
 * Workflow's newer version may label, key or type its inputs otherwise (Sol r1 S1): that start then stays a line.
 */
export function startInputsDeclared(workflows: readonly WorkflowSummary[] | null, workflow: string): readonly WorkflowInputSummary[] | null {
  return workflows?.find((listed) => listed.reference === workflow)?.inputs ?? null;
}

/** One text a person wrote into a Run's start form, under the input's label. */
export interface StartTextInput {
  readonly destination: string;
  readonly label: string;
  readonly text: string;
}

/**
 * What the person who started a Run wrote into its start form (Johannes, 4 Nov 15:36): the start's values, stored by
 * destination, that fill an input the Workflow declares for text only, in the order it declares them, each with its
 * label. Item references, numbers and the subject are not words a person wrote, so they stay out, as do values no
 * declared input names and empty ones. `declared` is `null` while the Workflow's inputs aren't known.
 */
export function startTextInputs(values: Readonly<Record<string, unknown>> | undefined, declared: readonly WorkflowInputSummary[] | null): readonly StartTextInput[] {
  if (values === undefined || declared === null) return [];
  const text = literalType("string");
  return declared.flatMap((input) => {
    const value = values[input.destination];
    const takesText = input.accepts.length > 0 && input.accepts.every((accepted) => accepted.type === text);
    return takesText && typeof value === "string" && value.trim() !== "" ? [{ destination: input.destination, label: input.label, text: value }] : [];
  });
}
